import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import { AuthActions, AuthSelectors } from '@fantaz/shared/auth';
import { CoreSnackbarActions } from '@fantaz/core/state';
import { CoreGoogleAnalyticActions } from '@fantaz/core/analytic';
import {
  CaptureLocationService,
  DataAccessAccountService,
  AccountService,
  ProfileApiService,
  VerificationDuplicateException,
  VerificationEscalateException,
  VerificationException,
  VerificationIncompleteException,
} from '@fantaz/shared/data-access/api';
import { CreditCard, GAEventKeys } from '@fantaz/shared/data-access/models';
import { CreditCardRegisterDialogComponent } from '@fantaz/user-verification/ui/register-credit-card-dialog';
import { RegisterCreditCardFailureDialogComponent } from '@fantaz/user-verification/ui/register-credit-card-failure-dialog';
import { LocationService } from '@fantaz/core/utils';

import { VerifyUserActions } from '../verify-user';
import * as RegisterCreditCardActions from './register-credit-card.actions';
import { VerifyUserWithCreditActions } from './register-credit-card.actions';
import * as RegisterCreditCardSelectors from './register-credit-card.selectors';

@Injectable()
export class RegisterCreditCardEffects {
  /**
   * Effect to opens the register credit card dialog in verify user flow
   */
  openCreditCardDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RegisterCreditCardActions.openRegisterCreditCardDialog),
      withLatestFrom(
        this.store.select(AuthSelectors.getAuthUser),
        (_, user) => ({
          user,
        }),
      ),
      switchMap(({ user }) => {
        const dialogRef = this.dialog.open(CreditCardRegisterDialogComponent, {
          disableClose: true,
          data: {
            initialData: {
              firstName: user?.firstName,
              lastName: user?.lastName,
              name: `${user?.firstName} ${user?.lastName}`,
              address: user?.legalAddress,
            },
            userGameProfile: {},
            closeSignal$: combineLatest(
              [
                this.store.select(
                  RegisterCreditCardSelectors.isVerifiedCreditCard,
                ),
                this.store.select(
                  RegisterCreditCardSelectors.getRegisterCreditCardError,
                ),
              ],
              (isVerified, error) => {
                return isVerified || !!error;
              },
            ),
            submitting$: this.store.select(
              RegisterCreditCardSelectors.isVerifyingCreditCard,
            ),
            error$: this.store.select(
              RegisterCreditCardSelectors.getRegisterCreditCardError,
            ),
            onSubmitCard: (creditCard: CreditCard) => {
              this.store.dispatch(
                RegisterCreditCardActions.verifyUserWithCreditCard({
                  creditCard,
                }),
              );
            },
          },
        });
        return dialogRef.afterClosed();
      }),
      mergeMap((result: any) => {
        if (result?.source === 'button') {
          return [VerifyUserActions.openVerificationDialog({})];
        }

        return [
          CoreGoogleAnalyticActions.trackEvent({
            event: {
              event: 'user_close_register_credit_card_dialog',
            },
          }),
        ];
      }),
    ),
  );

  /**
   * Effect to verify user with credit card, this effect is triggered when user
   * submit the credit card form
   */
  verifyUserWithCreditCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RegisterCreditCardActions.verifyUserWithCreditCard),
      switchMap(({ creditCard }) => {
        console.log(
          '[Verify User With Credit Card] \u001b[32m Start \u001b[0m',
        );
        return [
          VerifyUserWithCreditActions.saveUserProfile({
            creditCard,
          }),
          AuthActions.updateUserLocally({
            user: {
              firstName: creditCard.firstName,
              lastName: creditCard.lastName,
              legalAddress: creditCard.address,
              shippingAddress: creditCard.address,
            },
          }),
        ];
      }),
    ),
  );

  /**
   * Effect to handle save user profile on verify user with credit card
   * Verify User With Credit Card Step 1
   */
  saveUserProfileOnVerifyUserWithCreditCardEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserWithCreditActions.saveUserProfile),
      switchMap(({ creditCard }) => {
        console.log(
          '[Verify User With Credit Card] \u001b[32m Saving User Profile \u001b[0m',
        );
        return this.profileApiService
          .updateUserProfile({
            firstName: creditCard.firstName,
            lastName: creditCard.lastName,
            legalAddress: creditCard.address,
            shippingAddress: creditCard.address,
          })
          .pipe(
            map(() => {
              return VerifyUserWithCreditActions.saveUserProfileSuccess({
                creditCard,
              });
            }),
            catchError((err) => {
              console.log(
                '[Verify User With Credit Card] \u001b[32m Saving User Profile Failure\u001b[0m',
              );
              return [
                VerifyUserWithCreditActions.saveUserProfileFailure({
                  error: err,
                  creditCard,
                }),
              ];
            }),
          );
      }),
    ),
  );

  /**
   * Effect to handle verify user with credit card on save user profile success
   * Verify User With Credit Card Step 2
   */
  verifyUserProfileAfterSaveUserProfileEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserWithCreditActions.saveUserProfileSuccess),
      withLatestFrom(this.store.select(AuthSelectors.getAuthUser)),
      switchMap(([{ creditCard }, user]) => {
        return this.locationService.observeLocation().pipe(
          map((userLocation) => ({
            creditCard,
            user,
            userLocation,
          })),
        );
      }),
      switchMap(({ creditCard, user, userLocation }) => {
        console.log(
          '[Verify User With Credit Card] \u001b[32m Verify Credit Card \u001b[0m',
        );
        return this.accountService
          .verifyAccountWithCreditCard({
            user: { uuid: user?.uuid as string },
            card: creditCard,
            userLocation,
          })
          .pipe(
            mergeMap(() => [
              RegisterCreditCardActions.verifyUserWithCreditCardSuccess(),
              VerifyUserActions.verifyUserSuccess({ source: 'cc' }),
              CoreGoogleAnalyticActions.trackEvent({
                event: {
                  event: GAEventKeys.PAYMENT_REGISTRATION,
                  payment_registration_status: 'registration_success',
                },
              }),
            ]),
            catchError((error) => {
              console.log(
                '[Verify User With Credit Card] \u001b[32m Verify Credit Card Failure\u001b[0m',
              );
              return [
                VerifyUserWithCreditActions.verifyCreditCardFailure({
                  error,
                }),
              ];
            }),
          );
      }),
    ),
  );

  /**
   * Effect to handle kyc fails on edit info
   * Edit Info Step 3
   */
  handleVerifyUserProfileFailuresEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        VerifyUserWithCreditActions.saveUserProfileFailure,
        VerifyUserWithCreditActions.verifyCreditCardFailure,
      ),
      switchMap(({ error }) => {
        let attempts;

        if (error instanceof VerificationIncompleteException) {
          attempts = error?.attempts;
        }
        return [
          RegisterCreditCardActions.verifyUserWithCreditCardFailure({ error }),
          ...(attempts !== null
            ? [
                AuthActions.updateUserLocally({
                  user: {
                    //
                    verificationStatus: {
                      attempts,
                    },
                  },
                }),
              ]
            : []),
        ];
      }),
    ),
  );

  handleVerifyUserWithCreditCardFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RegisterCreditCardActions.verifyUserWithCreditCardFailure),
      withLatestFrom(this.store.select(AuthSelectors.getAuthUser)),
      filter(([{ error }]) => {
        return error instanceof VerificationException;
      }),
      switchMap(([{ error }, user]) => {
        if (
          error instanceof VerificationEscalateException ||
          error instanceof VerificationDuplicateException
        ) {
          return [
            VerifyUserActions.verifyUserFailure({ error }),
            CoreGoogleAnalyticActions.trackEvent({
              event: {
                event: GAEventKeys.USER_VERIFICATION_STATUS,
                verification_action:
                  error instanceof VerificationEscalateException
                    ? 'verification-escalate'
                    : 'verification-duplicate',
                verification_status: 'user-cc-verification-fail',
                verification_attempts_failed:
                  user?.verificationStatus?.attempts?.current.DOCUMENT || 0,
              },
            }),
          ];
        }
        return [
          RegisterCreditCardActions.openRegisterCreditCardFailureDialog({
            error,
          }),
          CoreGoogleAnalyticActions.trackEvent({
            event: {
              event: GAEventKeys.PAYMENT_REGISTRATION,
              payment_registration_status: 'registration_fail',
              payment_registration_type: 'credit-card',
            },
          }),
        ];
      }),
    ),
  );

  /**
   * Effect to handle incomplete verification in verify user flow
   */
  handleIncompleteVerifyUserWithCreditCardFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RegisterCreditCardActions.verifyUserWithCreditCardFailure),
      filter(({ error }) => error instanceof VerificationIncompleteException),
      switchMap(({ error }) => {
        return [
          AuthActions.updateUserLocally({
            user: {
              verificationStatus: {
                attempts: (error as unknown as VerificationIncompleteException)
                  .attempts,
              },
            },
          }),
          CoreSnackbarActions.showSnackbarError({
            message: error.message,
          }),
        ];
      }),
    ),
  );

  /**
   * Effect to opens the register credit card failure dialog in verify user flow
   */
  openRegisterCreditCardFailureDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RegisterCreditCardActions.openRegisterCreditCardFailureDialog),
      switchMap(({ error }) => {
        const dialogRef = this.dialog.open(
          RegisterCreditCardFailureDialogComponent,
          {
            data: {
              attempts: {
                ...(error as unknown as VerificationIncompleteException)
                  ?.attempts,
              },
            },
          },
        );
        return dialogRef.afterClosed();
      }),
      mergeMap((result?: { source?: string; creditCard?: any }) => {
        if (result?.source === 'edit-credit-card') {
          return [
            RegisterCreditCardActions.openRegisterCreditCardDialog({
              creditCard: result?.creditCard,
            }),
          ];
        }

        return [VerifyUserActions.openVerificationDialog({})];
      }),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly dialog: MatDialog,
    private readonly store: Store,
    @Inject(DataAccessAccountService)
    private readonly accountService: AccountService,
    private readonly locationService: LocationService,
    private readonly profileApiService: ProfileApiService,
  ) {}
}
